import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Users, BarChart2, TrendingUp, UserCheck, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageLoadingState } from '@/components/layout/PageLoadingState';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import { Button } from '@/components/ui/button';
import { DashboardMetricCard } from '@/components/dashboard/DashboardMetricCard';
import PeopleFilters, { PeopleFilterState } from '@/components/people/PeopleFilters';
import PeopleTable from '@/components/people/PeopleTable';
import { useAuth } from '@/hooks/useAuthApi';
import { useOrganization } from '@/hooks/useOrganization';
import { usePeopleList } from '@/hooks/people/usePeopleList';
import { usePeopleMetrics } from '@/hooks/people/usePeopleMetrics';
import peopleService from '@/lib/api/people';
import { useToast } from '@/components/ui/use-toast';

const PAGE_SIZE = 50;

const initialFilters: PeopleFilterState = {
  search: '',
  buyingIntent: [],
  qualificationStatus: [],
  datePreset: 'last_30_days',
};

const PeoplePage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();
  const { toast } = useToast();

  const [filters, setFilters] = useState<PeopleFilterState>(() => ({ ...initialFilters }));
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [filters.buyingIntent, filters.qualificationStatus, filters.datePreset, debouncedSearch]);

  const dateFilters = useMemo(() => {
    if (filters.datePreset === 'all_time') {
      return { date_from: undefined, date_to: undefined };
    }
    const now = new Date();
    const days = filters.datePreset === 'last_7_days' ? 7
      : filters.datePreset === 'last_90_days' ? 90
      : 30;
    const from = new Date(now);
    from.setDate(from.getDate() - days);
    return {
      date_from: from.toISOString(),
      date_to: now.toISOString(),
    };
  }, [filters.datePreset]);

  const { people, total, isLoading, error } = usePeopleList(currentOrganization?.id, {
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    search: debouncedSearch || undefined,
    buying_intent: filters.buyingIntent.length > 0 ? filters.buyingIntent.join(',') : undefined,
    qualification_status: filters.qualificationStatus.length > 0 ? filters.qualificationStatus.join(',') : undefined,
    date_from: dateFilters.date_from,
    date_to: dateFilters.date_to,
    enabled: !!currentOrganization?.id,
  });

  const { data: metrics, isLoading: metricsLoading } = usePeopleMetrics(
    currentOrganization?.id,
    { date_from: dateFilters.date_from, date_to: dateFilters.date_to },
  );

  // Keep page in bounds
  useEffect(() => {
    if (!total) return;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (page > totalPages) setPage(1);
  }, [page, total]);

  const clearFilters = useCallback(() => {
    setFilters({ ...initialFilters });
  }, []);

  const handleExportCsv = useCallback(async () => {
    if (!currentOrganization?.id) return;
    setIsExporting(true);
    try {
      await peopleService.exportPeopleCsv(currentOrganization.id, {
        search: debouncedSearch || undefined,
        buying_intent: filters.buyingIntent.length > 0 ? filters.buyingIntent.join(',') : undefined,
        qualification_status: filters.qualificationStatus.length > 0 ? filters.qualificationStatus.join(',') : undefined,
        date_from: dateFilters.date_from,
        date_to: dateFilters.date_to,
      });
    } catch (e) {
      console.error('CSV export failed:', e);
      toast({
        title: 'CSV export failed',
        description: 'Unable to download the export right now. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  }, [currentOrganization?.id, debouncedSearch, filters, dateFilters, toast]);

  if (authLoading || orgLoading) {
    return <PageLoadingState />;
  }

  if (!user) {
    return <div className="p-4 text-center">Please log in to view people.</div>;
  }

  if (!currentOrganization?.id) {
    return <div className="p-4 text-center">Select an organization to view people.</div>;
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <Helmet>
        <title>People | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
        <PageHeader title={<>People<HelpTooltip text="People are visitors who interacted with your demo agent during a session. Each person is identified by the information they provide during the conversation." /></>} />

        {/* Metrics */}
        <div className="px-4 sm:px-6 md:px-8 pt-6 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <DashboardMetricCard
              title="Total Users"
              value={metricsLoading ? '...' : (metrics?.total_users ?? 0)}
              icon={Users}
              iconBgColor="bg-blue-100 dark:bg-blue-900/40"
              change={metrics?.total_users_change != null ? {
                value: Math.abs(metrics.total_users_change),
                isPositive: metrics.total_users_change >= 0,
              } : undefined}
            />
            <DashboardMetricCard
              title="Avg Sessions / User"
              value={metricsLoading ? '...' : (metrics?.avg_demos_per_user ?? 0)}
              icon={BarChart2}
              iconBgColor="bg-green-100 dark:bg-green-900/40"
              change={metrics?.avg_demos_per_user_change != null ? {
                value: Math.abs(metrics.avg_demos_per_user_change),
                isPositive: metrics.avg_demos_per_user_change >= 0,
              } : undefined}
            />
            <DashboardMetricCard
              title="High-Intent"
              value={metricsLoading ? '...' : (metrics?.high_intent_users ?? 0)}
              icon={TrendingUp}
              iconBgColor="bg-orange-100 dark:bg-orange-900/40"
              change={metrics?.high_intent_users_change != null ? {
                value: Math.abs(metrics.high_intent_users_change),
                isPositive: metrics.high_intent_users_change >= 0,
              } : undefined}
            />
            <DashboardMetricCard
              title="Qualified Leads"
              value={metricsLoading ? '...' : (metrics?.qualified_leads ?? 0)}
              icon={UserCheck}
              iconBgColor="bg-purple-100 dark:bg-purple-900/40"
              change={metrics?.qualified_leads_change != null ? {
                value: Math.abs(metrics.qualified_leads_change),
                isPositive: metrics.qualified_leads_change >= 0,
              } : undefined}
            />
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 sm:px-6 md:px-8 pb-4">
          <PeopleFilters
            filters={filters}
            onFiltersChange={(patch) => setFilters((prev) => ({ ...prev, ...patch }))}
            onReset={clearFilters}
            onExportCsv={handleExportCsv}
            isExporting={isExporting}
          />
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto px-4 sm:px-6 md:px-8">
          {error && (
            <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error instanceof Error ? error.message : 'Failed to load people'}
            </div>
          )}
          <div className="rounded-xl border card-border card-bg">
            <PeopleTable
              people={people}
              isLoading={isLoading}
              offset={(page - 1) * PAGE_SIZE}
            />
          </div>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between py-4">
              <span className="text-sm text-muted-foreground">
                Showing {(page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PeoplePage;
